import { useCallback, useEffect, useRef, useState } from "react";
import { api, uploadThumbUrl } from "../api";
import type { UploadItem } from "../types";
import { IconClose, IconImage } from "./icons";
import { Spinner, cx } from "./ui";

export function ReferenceUploader({
  items,
  onChange,
  onError,
  disabled,
}: {
  items: UploadItem[];
  onChange: (items: UploadItem[]) => void;
  onError: (message: string) => void;
  disabled?: boolean;
}) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const zoneRef = useRef<HTMLDivElement>(null);

  const add = useCallback(
    async (files: File[]) => {
      const images = files.filter((f) => /^image\/(png|jpe?g|webp)$/.test(f.type));
      if (images.length === 0) return;
      setBusy(true);
      try {
        const uploaded = await api.upload(images.slice(0, 8));
        onChange([...items, ...uploaded].slice(0, 8));
      } catch (err) {
        onError(err instanceof Error ? err.message : "アップロードに失敗しました");
      } finally {
        setBusy(false);
      }
    },
    [items, onChange, onError],
  );

  // クリップボードから直接貼り付け
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (disabled) return;
      const target = e.target as HTMLElement | null;
      const inZone = zoneRef.current?.contains(target ?? null);
      const inText = target?.tagName === "TEXTAREA" || target?.tagName === "INPUT";
      if (inText && !inZone) return;
      const files = Array.from(e.clipboardData?.files ?? []);
      if (files.length) {
        e.preventDefault();
        void add(files);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [add, disabled]);

  return (
    <div className="space-y-2">
      <div
        ref={zoneRef}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void add(Array.from(e.dataTransfer.files));
        }}
        onClick={() => inputRef.current?.click()}
        className={cx(
          "flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed py-5 text-[13.5px] transition",
          dragging
            ? "border-brand-400 bg-brand-50 text-brand-700"
            : "border-slate-200 bg-slate-50/60 text-slate-500 hover:border-brand-300 hover:bg-brand-50/40",
        )}
      >
        {busy ? <Spinner className="h-4 w-4 text-brand-500" /> : <IconImage className="h-4 w-4" />}
        <span>
          {busy ? "アップロード中…" : "ドロップ / クリック / "}
          {!busy && <kbd className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[11.5px]">Ctrl+V</kbd>}
          {!busy && " で貼り付け"}
        </span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        hidden
        onChange={(e) => {
          void add(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />

      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((u) => (
            <div key={u.id} className="group relative h-16 w-16 overflow-hidden rounded-lg border border-slate-200">
              <img src={uploadThumbUrl(u.url, 192)} alt={u.name} loading="lazy" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => onChange(items.filter((i) => i.id !== u.id))}
                className="absolute right-0.5 top-0.5 grid h-5 w-5 place-items-center rounded-full bg-slate-900/70 text-white opacity-0 transition group-hover:opacity-100"
                aria-label="削除"
              >
                <IconClose className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
